// Festivals from around the world, at least one in every month. On those days the gallery
// decorates itself (lanterns, lamps, petals, kites, pumpkins, fireworks…) and greets the visitor.
// The decorations are made in Festivals.jsx; this file only says *which* festival it is today.
//
// Preview any festival with ?festival=<id>, e.g. ?festival=diwali.
//
// Festivals on the lunar or lunisolar calendars move every year, so their dates are listed for
// 2026–2030 (the gallery simply skips them after that until the table is extended). Easter,
// Carnival and Thanksgiving are worked out for any year.

// [month, day] pairs per year for the moving festivals.
const LUNAR_NEW_YEAR = { 2026: [2, 17], 2027: [2, 6], 2028: [1, 26], 2029: [2, 13], 2030: [2, 3] };
const HOLI = { 2026: [3, 4], 2027: [3, 22], 2028: [3, 11], 2029: [3, 1], 2030: [3, 20] };
const EID_AL_FITR = { 2026: [3, 20], 2027: [3, 10], 2028: [2, 27], 2029: [2, 14], 2030: [2, 5] };
const MID_AUTUMN = { 2026: [9, 25], 2027: [9, 15], 2028: [10, 3], 2029: [9, 22], 2030: [9, 12] };
const DIWALI = { 2026: [11, 8], 2027: [10, 29], 2028: [10, 17], 2029: [11, 5], 2030: [10, 26] };
const HANUKKAH = { 2026: [12, 5], 2027: [12, 25], 2028: [12, 13], 2029: [12, 2], 2030: [12, 21] }; // first full day

const dayNumber = (y, m, d) => Math.floor(Date.UTC(y, m - 1, d) / 86400000);
const today = (date) => dayNumber(date.getFullYear(), date.getMonth() + 1, date.getDate());

// Easter Sunday (Gregorian), the "anonymous" algorithm.
export const easterSunday = (y) => {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return [month, day];
};

// US Thanksgiving: the fourth Thursday of November.
const thanksgiving = (y) => {
  const first = new Date(y, 10, 1).getDay();
  return [11, 1 + ((4 - first + 7) % 7) + 21];
};

// Does `date` fall within [start - before, start + after] days of a [month, day] in its year?
const near = (date, md, before = 0, after = 0) => {
  if (!md) return false;
  const t = today(date), s = dayNumber(date.getFullYear(), md[0], md[1]);
  return t >= s - before && t <= s + after;
};
const fromTable = (table, before, after) => (date) => near(date, table[date.getFullYear()], before, after);
const fixed = (m, d, before = 0, after = 0) => (date) => near(date, [m, d], before, after);

// Each festival: when it is, a greeting, and which decorations to put up (see Festivals.jsx):
//   lanterns  colours of lanterns hanging under the glass roof
//   lamps     little oil lamps / candles glowing along the walls
//   petals    colours of petals / confetti / leaves drifting down inside the hall
//   fireworks colours of fireworks over the mountains (and the sky lights up)
//   balloons  colours of balloons rising past the windows
//   kites     colours of kites flying over the lawn
//   lawn      'pumpkins' or 'eggs' set out on the lawn outside the windows
//   tree      a decorated Christmas tree in the hall
//   carpet    colours of a flower carpet / rangoli on the floor
export const FESTIVALS = [
  // January
  { id: 'newyear', name: 'New Year', month: 'January', icon: '🎆', when: (d) => near(d, [1, 1]) || near(d, [12, 31]),
    greeting: 'Happy New Year! Fireworks over the mountains to welcome it in.',
    fireworks: ['#ffd166', '#ffffff', '#f72585', '#4cc9f0'], petals: ['#ffd166', '#f5f5f5', '#e0b84a'] },
  { id: 'pongal', name: 'Pongal & Makar Sankranti', month: 'January', icon: '🪁', when: fixed(1, 14, 0, 1),
    greeting: 'Happy Pongal and Makar Sankranti! Kites are flying over the meadow.',
    kites: ['#ff006e', '#ffbe0b', '#3a86ff', '#06d6a0', '#fb5607'], carpet: ['#ffffff', '#ff006e', '#ffbe0b', '#3a86ff'] },
  { id: 'lunarnewyear', name: 'Lunar New Year', month: 'January / February', icon: '🏮', when: fromTable(LUNAR_NEW_YEAR, 1, 2),
    greeting: 'Happy Lunar New Year! Red lanterns for luck and fireworks for a bright year.',
    lanterns: ['#d00000', '#e5383b', '#d00000', '#ffba08'], fireworks: ['#ff0000', '#ffd166', '#ffffff'] },
  // February
  { id: 'valentine', name: "Valentine's Day", month: 'February', icon: '💝', when: fixed(2, 14),
    greeting: "Happy Valentine's Day! Love is in the air (and in every painting).",
    balloons: ['#e63946', '#ff4d6d', '#ff8fa3', '#c9184a'], petals: ['#e63946', '#ff8fa3', '#ffccd5'] },
  { id: 'carnival', name: 'Carnival', month: 'February / March', icon: '🎭',
    when: (d) => { const [m, day] = easterSunday(d.getFullYear()); return near(d, [m, day - 47], 4, 0); },
    greeting: 'Carnival time! Confetti and colour, from Rio to Venice.',
    petals: ['#7b2cbf', '#ffd60a', '#06d6a0', '#ff006e', '#3a86ff'], balloons: ['#7b2cbf', '#ffd60a', '#06d6a0', '#ff006e'] },
  // March
  { id: 'holi', name: 'Holi', month: 'March', icon: '🎨', when: fromTable(HOLI, 0, 1),
    greeting: 'Happy Holi! The festival of colours, the most painterly festival of all.',
    petals: ['#ff006e', '#ffbe0b', '#3a86ff', '#8338ec', '#06d6a0', '#fb5607'], balloons: ['#ff006e', '#ffbe0b', '#3a86ff', '#8338ec', '#06d6a0'] },
  { id: 'eid', name: 'Eid al-Fitr', month: 'varies', icon: '🌙', when: fromTable(EID_AL_FITR, 0, 2),
    greeting: 'Eid Mubarak! Lanterns glow in the gallery tonight.',
    lanterns: ['#ffd166', '#2a9d8f', '#e9c46a', '#ffffff'], lamps: true },
  { id: 'stpatrick', name: "St Patrick's Day", month: 'March', icon: '☘️', when: fixed(3, 17),
    greeting: "Happy St Patrick's Day! Everything's a little greener today.",
    petals: ['#2b9348', '#55a630', '#80b918', '#ffffff'], balloons: ['#2b9348', '#55a630', '#ffffff', '#f77f00'] },
  // April
  { id: 'hanami', name: 'Hanami', month: 'April', icon: '🌸', when: fixed(4, 1, 0, 9),
    greeting: 'Hanami: cherry blossom season. Petals drift through the gallery.',
    petals: ['#ffc8dd', '#ffafcc', '#ffe5ec', '#fb6f92'] },
  { id: 'easter', name: 'Easter', month: 'March / April', icon: '🐣', when: (d) => near(d, easterSunday(d.getFullYear()), 2, 1),
    greeting: 'Happy Easter! Someone has hidden painted eggs on the lawn. (Yes, real Easter eggs.)',
    lawn: 'eggs', petals: ['#ffd6a5', '#caffbf', '#9bf6ff', '#ffc6ff'] },
  // May
  { id: 'koinobori', name: "Children's Day (Kodomo no Hi)", month: 'May', icon: '🎏', when: fixed(5, 5, 1, 0),
    greeting: "Happy Children's Day! Carp streamers are flying in the wind for every child.",
    kites: ['#1d3557', '#e63946', '#f4a261', '#2a9d8f'] },
  // June
  { id: 'midsummer', name: 'Midsummer', month: 'June', icon: '🌼', when: fixed(6, 20, 0, 4),
    greeting: 'Happy Midsummer! The longest days of the year, and flowers everywhere.',
    petals: ['#ffffff', '#ffd60a', '#ffc8dd', '#a2d2ff'], carpet: ['#ffd60a', '#ffffff', '#55a630', '#ffc8dd'] },
  // July
  { id: 'tanabata', name: 'Tanabata', month: 'July', icon: '🎋', when: fixed(7, 7),
    greeting: 'Happy Tanabata, the star festival! Paper wishes hang from the roof tonight.',
    lanterns: ['#ffadad', '#ffd6a5', '#fdffb6', '#caffbf', '#9bf6ff', '#bdb2ff'], meteors: true },
  // August
  { id: 'obon', name: 'Obon', month: 'August', icon: '🏮', when: fixed(8, 13, 0, 3),
    greeting: 'Obon: lanterns are lit to welcome and remember loved ones.',
    lanterns: ['#ffffff', '#ffd6a5', '#ffadad'], lamps: true },
  // September
  { id: 'midautumn', name: 'Mid-Autumn Festival', month: 'September / October', icon: '🥮', when: fromTable(MID_AUTUMN, 1, 1),
    greeting: 'Happy Mid-Autumn Festival! Lanterns glow under the harvest moon.',
    lanterns: ['#ff7b00', '#ffd000', '#e5383b', '#ff9e00'] },
  // October / November
  { id: 'diwali', name: 'Diwali', month: 'October / November', icon: '🪔', when: fromTable(DIWALI, 1, 1),
    greeting: 'Happy Diwali! Diyas glow along the walls, there is a rangoli on the floor and fireworks outside.',
    lamps: true, lanterns: ['#ff9e00', '#ffd000', '#ff006e', '#ff7b00'], fireworks: ['#ffd166', '#ff9e00', '#ffffff', '#f72585'],
    carpet: ['#ff006e', '#ffbe0b', '#fb5607', '#8338ec', '#ffffff'] },
  { id: 'halloween', name: 'Halloween', month: 'October', icon: '🎃', when: fixed(10, 31, 1, 0),
    greeting: 'Happy Halloween! Jack-o\'-lanterns are grinning on the lawn.',
    lawn: 'pumpkins', lanterns: ['#ff7b00', '#7b2cbf', '#ff7b00'], petals: ['#ff7b00', '#3c096c', '#000000'] },
  { id: 'muertos', name: 'Día de los Muertos', month: 'November', icon: '💀', when: fixed(11, 1, 0, 1),
    greeting: 'Día de los Muertos: marigold petals and candles to remember those we love.',
    petals: ['#ff9e00', '#ffb700', '#ff6d00'], lamps: true, carpet: ['#ff9e00', '#ffb700', '#ff006e', '#7b2cbf'] },
  { id: 'thanksgiving', name: 'Thanksgiving', month: 'November', icon: '🍂', when: (d) => near(d, thanksgiving(d.getFullYear()), 0, 1),
    greeting: 'Happy Thanksgiving! Thank you for visiting the gallery.',
    petals: ['#bc4b00', '#e85d04', '#dc2f02', '#ffba08', '#9d0208'] },
  // December
  { id: 'hanukkah', name: 'Hanukkah', month: 'December', icon: '🕎', when: fromTable(HANUKKAH, 1, 7),
    greeting: 'Happy Hanukkah! Candles glow along the walls for the festival of lights.',
    lamps: true, lanterns: ['#4895ef', '#ffffff', '#ffd166'] },
  { id: 'christmas', name: 'Christmas', month: 'December', icon: '🎄', when: fixed(12, 24, 0, 2),
    greeting: 'Merry Christmas! There is a tree in the hall and snowflakes drifting down.',
    tree: true, petals: ['#ffffff', '#f1faee', '#e0f2ff'], lanterns: ['#d00000', '#2b9348', '#ffd166'] },
];

// Today's festival, or null. ?festival=<id> previews one. When two overlap (Easter during the
// cherry blossom, Christmas during Hanukkah), the one that lasts fewer days wins today.
const spanDays = (f, date) => {
  let n = 0;
  for (let k = -10; k <= 10; k++) { const d = new Date(date); d.setDate(d.getDate() + k); if (f.when(d)) n++; }
  return n;
};
export const festivalToday = (date = new Date()) => {
  if (typeof window !== 'undefined') {
    const m = window.location.search.match(/[?&]festival=([a-z]+)/);
    if (m) return FESTIVALS.find((f) => f.id === m[1]) || null;
  }
  const today_ = FESTIVALS.filter((f) => f.when(date));
  if (today_.length < 2) return today_[0] || null;
  return today_.reduce((best, f) => (spanDays(f, date) < spanDays(best, date) ? f : best));
};
